import { useCallback, useEffect, useRef, useState } from "react";
import { synthesizeSpeech } from "../api/ttsClient";
import type { TtsPlayer } from "../audio/ttsPlayer";
import { getTtsPlayer } from "../audio/ttsPlayerInstance";
import { useChatStore } from "../state/chatStore";
import { stripSsmlTags } from "../lib/ssml";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";

// AI の声 (spec 2026-07-28 §3.5)。読み上げに関する状態をここ 1 箇所に閉じる:
// 未読のキュー・再生位置・保持 (割り込み判定待ち)・SSML の差し替え・再生状態の表示。
//
// キューにするのが要点 (D7)。以前は messages が変わるたびに新しい再生ループが起動し、
// 複数のループが 1 つの TtsPlayer と 1 つの世代カウンタを奪い合っていた。そのため
// 後から吹き出しが 1 つ append されるだけで、直前の未読がまとめて読み飛ばされていた
// (毎ターン お礼 + 次の質問 が同時に積まれるので、次の質問が声で流れないことがあった)。
// 読み上げるのは常に 1 本だけにすれば、「追い越された」という状態自体が発生しない。

export type NarrationState = "idle" | "speaking" | "held";

export type ResumeReason =
  | "empty"
  | "loopback"
  | "backchannel"
  | "llm-no-value"
  | "llm-loopback"
  | "short-utterance"
  | "error"
  | "timeout"
  // 設問がまだ一度も鳴っていないのに話した (お礼 / greeting の再生中の言い足し)。
  // その設問への回答としては受理せず、読み上げを続ける。
  | "not-yet-asked";

type Speakable = { id: string; kind: string; text: string };

/**
 * `tts.spoken` の結末 (spec §6.8)。**5 値に割るのが要点。**
 *
 * `never-played` (合成中に捨てられ 1 音も鳴っていない) と `cut-off` (途中まで鳴って
 * 止められた) を混ぜると、このイベントが答えるはずの問い —「その人はこの設問を
 * 聞いたのか」— にそのまま答えられなくなる。同様に `synth-failed` と `play-failed` も、
 * 前者は `app.error{context:"tts"}` に既に出るので、分けて初めて後者
 * (iOS の NotAllowedError / decode 失敗) が新しい情報になる。
 */
type TtsOutcome = "played" | "cut-off" | "never-played" | "synth-failed" | "play-failed";

/**
 * 実際に鳴った長さ (ms)。**観測のために読み上げを止めない** (原則 5)。
 *
 * ここで throw すると `speakOne` の外まで伝播し、`drain` のループごと死んで
 * **以降 1 件も読み上げられなくなる** (このリポジトリが 3 回踏んでいる「一度壊れると
 * 復帰できない無音」と同じ形)。計測値が 0 になるのは、声が出なくなるのに比べれば
 * はるかに軽い代償なので、握りつぶして 0 を返す。
 */
function audiblePositionMs(player: TtsPlayer): number {
  try {
    return Math.round(player.positionSec() * 1000);
  } catch {
    return 0;
  }
}

type Opts = {
  /** 読み上げ中・保持中は無音の再依頼を止める。continuous 未起動なら noop で渡す。 */
  pauseSilence: () => void;
  /** 読み上げ終了・再開で戻す。level を保持する resume 側を呼ぶこと (start は level を 0 に戻す)。 */
  resumeSilence: () => void;
};

// 判定が終わらなくても必ず読み上げを再開するまでの時間 (spec D6)。
// long の発話終了判定 1800ms + Scribe 確定 mean 508ms + 構造化 LLM mean 1470ms
// = 約 3.8 秒かかるので、これより短いと正常系で毎回タイムアウトする。
//
// **この予算は「話し終えてから」のもの** (spec 2026-08-04 D4)。保持が始まるのは発話
// 開始の 200ms 後なので、当初はそこから 6 秒を測っており、**発話の長さがそのまま予算から
// 引かれていた**。long 設問の回答は実測 3〜6 秒あるため、本番では 5.1 秒の回答で確定文が
// 届いた 0.6 秒後にタイムアウトし、判定を待たずに読み上げが再開していた
// (2026-08-04 の barge-in 4 件中 3 件が timeout)。発話が終わった時点で extendHold() が
// 測り直すので、値そのものは 6000 のまま据え置いてよい。
const HOLD_TIMEOUT_MS = 6000;

// 止まった瞬間に発音されかけていた語を聞き直せるように巻き戻す秒数 (spec D5)。
const REWIND_SEC = 1;

// 「同じ吹き出しで 3 回誤爆したら以降は保持しない」という安全弁 (spec D9) は
// 2026-07-28 のレビューで**撤回した**。保持しないと `isPlaying()` が true のままになり、
// `useContinuousMode` の「TTS 再生中は Scribe に送らない」ガードに掛かって、
// **その吹き出しが鳴り終わるまでユーザーの本物の回答が丸ごと捨てられる**
// (吹き出しも聞き直しも出ない = 直そうとしている症状そのもの)。
// 防ごうとしていたのは「読み上げが途切れ途切れになる」煩わしさで、招いた害の方が重い。
// 誤爆のたびに読み上げは必ず再開するので進行は止まらず、2 回目以降は巻き戻さないので
// 同じ箇所を聞き直させることもない。連続回数は計測 (`consecutive`) には引き続き載せる。

export function useNarration({ pauseSilence, resumeSilence }: Opts) {
  // 合成には数百 ms かかる。その間に保持・破棄されたら、遅れて届いた音声を鳴らしては
  // ならない (ユーザーの発話に AI の声が被さり、その発話が破棄される)。
  const generationRef = useRef(0);
  const queueRef = useRef<Speakable[]>([]);
  const drainingRef = useRef(false);
  // 読み上げ済みの id。**1 件ずつ持つ**のが要点で、「ここまで読んだ」を 1 つの
  // 目印 id で持ってはならない (spec §11.3)。目印方式はスレッドの中からその id を
  // 探す必要があり、スレッドが書き換わると行方不明になる。しかも「見つからない」の
  // 意味が経路によって正反対になる:
  //
  //   ↻ やり直す (切り詰め)  残るのは目印より前 = すべて既読 → 読んではいけない
  //   最初から (作り直し)     残るのはすべて新規          → 全部読まなければならない
  //
  // 目印だけではこの 2 つを区別できず、どちらに倒しても片方が壊れる。さらに
  // 見失った状態から復帰する手立てが無いため**一度壊れると以降ずっと無音**になる
  // (2026-07-28 の実機で「最初から」を選んだ後に発生)。1 件ずつ持てば、そもそも
  // 見失う対象が存在しない。
  //
  // 集合は増える一方だが、id は単調増加で再利用されない (`chatStore` の `nextId`) ため
  // 誤ヒットは起こらず、1 セッション分の会話量ではメモリも問題にならない。
  const spokenIdsRef = useRef<Set<string>>(new Set());
  // 画面表示は plain text、TTS には pause 入りの SSML を送る差し替え表。
  const ssmlOverrideRef = useRef<Record<string, string>>({});
  const currentRef = useRef<{ id: string; text: string } | null>(null);

  const heldRef = useRef(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldAtRef = useRef(0);
  const consecutiveRef = useRef(0);
  // 保持が解けるまで待つための約束。合成 (数百 ms) の最中に保持された場合、
  // pause() は鳴っていない音を止められないので、完成した音声が判定を待たずに
  // 鳴り出してしまう。それを待たせるために使う。
  const holdReleaseRef = useRef<{ promise: Promise<void>; release: () => void } | null>(null);

  const releaseHoldWaiters = useCallback(() => {
    holdReleaseRef.current?.release();
    holdReleaseRef.current = null;
  }, []);

  const [ttsState, setTtsState] = useState<Record<string, "idle" | "playing" | "failed">>({});
  const [state, setState] = useState<NarrationState>("idle");

  // prop は毎レンダー最新値へ同期する ref に鏡写しする。コールバックを useCallback で
  // 安定させたまま最新値を読むため (stale closure 対策)。
  const pauseSilenceRef = useRef(pauseSilence);
  pauseSilenceRef.current = pauseSilence;
  const resumeSilenceRef = useRef(resumeSilence);
  resumeSilenceRef.current = resumeSilence;

  const getPlayer = useCallback((): TtsPlayer => getTtsPlayer(), []);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current !== null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const emit = useCallback((type: string, payload: Record<string, unknown>) => {
    const sessionId = useChatStore.getState().sessionId;
    if (!sessionId) return;
    eventBus.emit(makeEvent(type, sessionId, payload));
  }, []);

  // 1 件を合成して鳴らす。戻り値の "abandoned" はキューごと捨てられたことを意味する。
  const speakOne = useCallback(
    async (messageId: string, kind: string, text: string): Promise<"played" | "abandoned"> => {
      // **既読にするのはここ (鳴らし始める瞬間)。** キューに積んだ時点で付けてはならない —
      // `abandon()` はキューを空にするが記録は戻さないので、キューで待っていた吹き出しが
      // 永久に未読判定へ引っかからなくなる (上位で何をしても取り返せない片道ラッチ)。
      // 逆に鳴らし終えてから付けると、再レンダーのたびに同じ吹き出しが積み直される。
      // 「鳴り始めたか」を境にすれば、捨てられた分は自然に未読へ戻り、
      // 一度でも聞かせた分は二度と読み直されない。
      spokenIdsRef.current.add(messageId);
      const generation = ++generationRef.current;
      const player = getPlayer();
      player.stop();
      // 新しい吹き出しに移ったので誤爆の連続カウントを 0 に戻す (spec D9)。
      if (currentRef.current?.id !== messageId) consecutiveRef.current = 0;
      currentRef.current = { id: messageId, text };
      setTtsState((s) => ({ ...s, [messageId]: "playing" }));
      setState("speaking");
      // 読み上げ中は user が黙って聞いている状態なので、無音時の再依頼を進めない。
      pauseSilenceRef.current();
      const ttsText = ssmlOverrideRef.current[messageId] ?? text;
      // 読み上げ文言そのものは payload に載せない (`answer.corrected` と同じ扱い) —
      // events.jsonl は 48KB の beacon trim を受けるので、長さの読めないテキストを置かない。
      // 文言の特定は kind + chars + timestamp で足りる。
      const chars = stripSsmlTags(ttsText).length;
      let model = "unknown";
      let synthMs = 0;
      let audibleMs = 0;
      let outcome: TtsOutcome = "played";
      try {
        // --- 合成 ---
        // **再生と別の try にするのが要点。** 1 つにまとめると「合成できなかった」と
        // 「音が鳴らせなかった」が同じ catch に落ちて区別できない。前者は
        // `app.error{context:"tts"}` に既に出るので、区別して初めて後者
        // (iOS の NotAllowedError / decode 失敗) が新しい情報になる。
        let audio: Blob;
        try {
          const synth = await synthesizeSpeech(ttsText);
          audio = synth.audio;
          model = synth.model;
          synthMs = synth.durationMs;
        } catch {
          setTtsState((s) => ({ ...s, [messageId]: "failed" }));
          outcome = "synth-failed";
          return "played"; // 合成に失敗しただけなので、続きは読む (原則 5)
        }
        // 合成の最中に保持されていたら、判定が済むまで鳴らさずに待つ。待たずに鳴らすと
        // ユーザーの発話に AI の声が被さり、その発話が破棄される (= 話しても反応しない)。
        // pause() では止められない — まだ音が始まっていないため。
        while (heldRef.current && holdReleaseRef.current) {
          await holdReleaseRef.current.promise;
        }
        // 破棄されていた場合は鳴らさない。**1 音も鳴っていない** ので "cut-off" と分ける。
        if (generation !== generationRef.current) {
          setTtsState((s) => ({ ...s, [messageId]: "idle" }));
          outcome = "never-played";
          return "abandoned";
        }
        // --- 再生 ---
        try {
          await player.play(audio);
        } catch {
          setTtsState((s) => ({ ...s, [messageId]: "failed" }));
          outcome = "play-failed";
          audibleMs = audiblePositionMs(player);
          return "played"; // 鳴らせなかっただけなので、続きは読む (原則 5)
        }
        // 再生位置は **play() から戻った直後に同期で読む**。読み上げは drain が
        // 1 本に直列化しているので、ここで読めば他の再生に上書きされない。
        // 壁時計を使ってはならない (保持の最大 6 秒がそのまま乗る)。
        audibleMs = audiblePositionMs(player);
        setTtsState((s) => ({ ...s, [messageId]: "idle" }));
        // 再生中に破棄された場合も (abandon が世代を進める) 中断として扱う。
        if (generation === generationRef.current) return "played";
        outcome = "cut-off";
        return "abandoned";
      } finally {
        // 所有権は世代で判定する。読み上げは drain が 1 本に直列化しているので
        // 現状ここが id 判定でも壊れないが、id で見ると「同じ吹き出しを 2 回読む」
        // 状況で負けた側が勝った側の記録を消しうる。消えると hold() が no-op になり、
        // 音は鳴っているのに一時停止できず、isPlaying() が true のまま発話が破棄される。
        // 直列化が将来崩れたときの番人として世代で判定しておく。
        if (generation === generationRef.current) currentRef.current = null;
        // 保持中に finally へ来ることはない (play() の Promise が解けないため) が、
        // 合成失敗などで抜けた場合に無音の再依頼が止まったままにならないよう必ず戻す。
        resumeSilenceRef.current();
        // **出口はここ 1 箇所**。上の 5 経路はすべて return するので、分岐ごとに
        // emit を置くと必ずどれかが漏れる (2026-08-04 に `rejectAnswer` の保持中分岐が
        // 何も出さず R2 に痕跡が残らなかったのと同じ形)。
        emit("tts.spoken", { messageId, kind, chars, model, synthMs, audibleMs, outcome });
      }
    },
    [getPlayer, emit]
  );

  // 読み上げは常に 1 本。走行中の enqueue はキューに積まれるだけなので、
  // 「別の読み上げに追い越される」状態が構造的に発生しない (spec D7)。
  const drain = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      for (;;) {
        const next = queueRef.current.shift();
        if (!next) break;
        // 戻り値が "abandoned" でもキューには触らない。捨てられたのはこの 1 件だけで、
        // キュー全体を捨てるかどうかは abandon() が自分で決めている (同期で空にする)。
        //
        // ここで消すと**「捨てた後に積まれたもの」まで巻き添えになる**。回答が確定した
        // ターンはまさにその形で、abandon() の直後に お礼 と 次の質問 が積まれる。
        // 読み上げループが後から目覚めて消すと、次の質問が一度も読まれない
        // (2026-07-28 の preview 実機で発生。1 問目の途中で答えると 2 問目が無音になった)。
        // 積まれていなければ次の shift が undefined を返して自然に抜ける。
        await speakOne(next.id, next.kind, next.text);
      }
    } finally {
      drainingRef.current = false;
      setState("idle");
    }
  }, [speakOne]);

  const enqueue = useCallback(
    (messages: Speakable[]) => {
      // まだ読んでいないものだけを拾う。スレッドがどう書き換わっていても、
      // 1 件ずつ独立に決まるので判断を誤りようがない。
      // ユーザーの発話は読み上げない (AI が回答を読み返してしまう)。
      //
      // 「既に鳴らした」に加えて「キューで待っている」も除く。再レンダーのたびに
      // 同じ配列が積み直されるのを防ぐのはこちらの条件で、既読の記録は
      // `speakOne` が鳴らし始める瞬間に付ける (そこのコメントを参照)。
      const unread = messages.filter(
        (m) =>
          m.kind !== "user-transcript" &&
          !spokenIdsRef.current.has(m.id) &&
          !queueRef.current.some((q) => q.id === m.id)
      );
      if (unread.length === 0) return;
      queueRef.current.push(...unread);
      void drain();
    },
    [drain]
  );

  /**
   * 読み上げ済みとして記録する。「続きから」で復元した過去の吹き出しに使う。
   *
   * **復元した分をすべて渡すこと。** 最後の 1 件だけを渡す形にすると、その手前が
   * 未読のまま残って読み上げられる。
   */
  const markSpoken = useCallback((ids: string[]) => {
    for (const id of ids) spokenIdsRef.current.add(id);
  }, []);

  /**
   * 1 件を今すぐ読む (🔊 リプレイ / 送信完了のしめくくり)。
   *
   * `speakOne` を直接呼ばずキューの先頭に積むのが要点。直接呼ぶと走行中の読み上げを
   * 止めることになり、**未読の「次の質問」がキューごと消える**。毎ターン お礼 と
   * 次の質問 が同時に積まれるので、お礼の最中にリプレイを押しただけで次の質問が
   * 一度も読まれなくなっていた (聞き逃した人が押すボタンが症状を再生産する)。
   * 先頭に積んで今の 1 件を譲らせれば、残りのキューはそのまま後に続く。
   */
  const speakNow = useCallback(
    (messageId: string, text: string) => {
      queueRef.current.unshift({ id: messageId, kind: "ai-replay", text });
      // 走行中の 1 件を譲らせる (世代が変わるので "abandoned" が返るが、
      // キューの世代は変えないので drain は次 = いま積んだ 1 件へ進む)。
      generationRef.current += 1;
      getTtsPlayer().stop();
      void drain();
    },
    [drain]
  );

  /**
   * 読み上げを一時停止して割り込みの判定を待つ。
   *
   * 同期であることが要件。非同期にすると発話終了の時点で isPlaying() が true のままに
   * なり、割り込んだ発話が丸ごと破棄される (報告された症状の再発)。
   */
  const hold = useCallback(() => {
    // 既に保持中 = 既に一時停止済み。何もしなくてよい。
    if (heldRef.current) return;
    // 何も鳴っていない。ここで早期 return しても isPlaying() は false なので、
    // 呼び出し側が発話を取りこぼすことはない。
    if (!currentRef.current) return;
    heldRef.current = true;
    heldAtRef.current = Date.now();
    setState("held");
    getPlayer().pause();
    if (!holdReleaseRef.current) {
      let release!: () => void;
      const promise = new Promise<void>((r) => {
        release = r;
      });
      holdReleaseRef.current = { promise, release };
    }
    pauseSilenceRef.current();
    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      // 判定が返ってこなくても必ず読み上げに戻る (spec D6、原則 5)。
      resumeRef.current("timeout");
    }, HOLD_TIMEOUT_MS);
  }, [getPlayer, clearHoldTimer]);

  const resume = useCallback(
    (reason: ResumeReason) => {
      if (!heldRef.current) return;
      heldRef.current = false;
      clearHoldTimer();
      releaseHoldWaiters();
      const heldMs = Date.now() - heldAtRef.current;
      consecutiveRef.current += 1;
      const messageId = currentRef.current?.id;
      // 2 回目以降は巻き戻さない (spec D5)。巻き戻し続けると、再開後すぐまた止まった
      // 場合に正味 0.8 秒ずつ後退し、同じところを何度も聞かされることになる。
      getPlayer().resume(consecutiveRef.current === 1 ? REWIND_SEC : 0);
      // ここで「再開」ではなく「停止」を呼ぶのが正しい。AI の声がまた聞こえ始めるので、
      // 無音の再依頼は止めておく必要がある。保持している間に発話終了が届いており、
      // そこで SilenceTimer.start() が呼ばれてタイマーが張り直されている
      // (useContinuousMode の onSpeechEnd)。止め直さないと、読み上げの最中に
      // 「聞こえていますでしょうか」が割り込む — 2026-07-09 に潰したバグが別の
      // 入口から戻ることになる。無音の再依頼は読み上げの終わり (speakOne の finally)
      // で戻る。
      pauseSilenceRef.current();
      setState("speaking");
      emit("bargein.resumed", {
        reason,
        heldMs,
        consecutive: consecutiveRef.current,
        ...(messageId ? { messageId } : {}),
      });
    },
    [getPlayer, clearHoldTimer, releaseHoldWaiters, emit]
  );

  // hold() のタイマーから resume() を呼ぶための橋渡し。hold は resume より前に
  // 宣言されるので、直接参照すると初期化前アクセスになる。
  const resumeRef = useRef(resume);
  resumeRef.current = resume;

  /**
   * 保持のタイムアウトを、呼ばれた時点から測り直す (spec 2026-08-04 D4/D5)。
   *
   * 呼ぶのは「発話が終わり、判定の待ち時間がここから始まる」瞬間だけ
   * (`useContinuousMode.onSpeechEnd`)。**`hold()` を呼び直す形にはしない** — hold は
   * 二重呼び出しを弾くことで「最後の網であるタイムアウトが延び続ける」のを防いでおり、
   * その性質を壊さずに起点だけ動かす必要がある。
   *
   * 保持していないときは何もしない。ここでタイマーを作ると、宙に浮いたそれが
   * 次の保持を途中で解いてしまう。
   */
  const extendHold = useCallback(() => {
    if (!heldRef.current) return;
    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      resumeRef.current("timeout");
    }, HOLD_TIMEOUT_MS);
  }, [clearHoldTimer]);

  /** 読み上げを破棄してキューも捨てる。割り込みが本物の回答だったとき。 */
  const abandon = useCallback(() => {
    const wasHeld = heldRef.current;
    const heldMs = wasHeld ? Date.now() - heldAtRef.current : 0;
    const messageId = currentRef.current?.id;
    heldRef.current = false;
    clearHoldTimer();
    releaseHoldWaiters();
    // キューを空にするのはここだけ。読み上げループ側では消さない (消すと、この直後に
    // 積まれる お礼 と 次の質問 まで巻き添えになる)。
    queueRef.current = [];
    generationRef.current += 1;
    // stop() で保留していた play() の Promise が解け、speakOne の finally が
    // 無音の再依頼を戻す。ここで重ねて呼ばない。
    getTtsPlayer().stop();
    setState("idle");
    if (wasHeld) {
      emit("bargein.confirmed", { heldMs, ...(messageId ? { messageId } : {}) });
    }
  }, [clearHoldTimer, releaseHoldWaiters, emit]);

  /**
   * その種類の吹き出しが、まだ一度も鳴らないままキューで待っているか。
   *
   * 毎ターン お礼 と 次の設問 が同時に積まれ、読み上げは 1 件ずつ直列に進むので、
   * お礼を鳴らしている約 2 秒のあいだ次の設問はキューで待っている。この窓で入った発話は
   * **問いを聞かないまま答えたことになる** — 受理すると、聞いてもいない問いの回答として
   * 記録され、しかもその問いは読まれないまま次へ進む。
   *
   * 設問 (`ai-question`) だけでなく「続きから確認」(`ai-resume-prompt`) と
   * 送信確認 (`ai-summary`) にも同じ窓があるので、呼び出し側が phase に応じて
   * 何を待っているかを渡す。
   *
   * **鳴っている最中のものは数えない。** 読み上げ中の割り込み (barge-in) は従来どおり
   * 受理する必要がある — 塞ぐのは「一度も鳴っていない」場合だけ。
   */
  const hasQueued = useCallback(
    (kind: string) => queueRef.current.some((m) => m.kind === kind),
    []
  );

  const isHeld = useCallback(() => heldRef.current, []);
  const isPlaying = useCallback(() => getTtsPlayer().isPlaying(), []);
  const currentMessageId = useCallback(() => currentRef.current?.id, []);
  const currentText = useCallback(() => currentRef.current?.text ?? "", []);

  /**
   * SSML 版を登録しつつ append する。画面には SSML を除いた plain text が出て、
   * TTS には pause 入りの SSML が送られる。
   */
  const appendWithSsml = useCallback(
    (
      base: { kind: "ai-confirm" | "ai-summary"; questionId?: string },
      ssmlText: string
    ): string => {
      const displayText = stripSsmlTags(ssmlText);
      const append = useChatStore.getState().append;
      const id =
        base.kind === "ai-confirm"
          ? append({ kind: "ai-confirm", questionId: base.questionId ?? "", text: displayText })
          : append({ kind: "ai-summary", text: displayText });
      ssmlOverrideRef.current[id] = ssmlText;
      return id;
    },
    []
  );

  const setSinkId = useCallback(
    (deviceId: string) => {
      // 出力先の適用は fire-and-forget (失敗しても既定出力で鳴る、原則 5)。
      void getPlayer()
        .setSinkId(deviceId)
        .catch(() => undefined);
    },
    [getPlayer]
  );

  // unmount で読み上げもタイマーも止める。
  useEffect(
    () => () => {
      clearHoldTimer();
      getTtsPlayer().stop();
    },
    [clearHoldTimer]
  );

  return {
    enqueue,
    markSpoken,
    speakNow,
    appendWithSsml,
    hold,
    resume,
    extendHold,
    abandon,
    hasQueued,
    isHeld,
    isPlaying,
    currentMessageId,
    currentText,
    setSinkId,
    ttsState,
    state,
  };
}
